import axios from 'axios';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from '../app/hooks';
import { USER_TYPE } from '../utils/constants/user';
import { Mixpanel } from '../utils/mixpanel';
import { hotjarIdentify } from '../utils/hotjar';
import { addTokenToHeaders } from '../utils/axios-interceptor';
import { setUserData } from '../store/user/actions';
import { fetchConfigAfterLogin } from '../store/user/userV2/actions';
import { handleRedirection } from '../store/user/userV2/authSaga';
import MagicUpload from '../common/MagicUpload/MagicUpload';
import Topbar from '../common/Topbar/Topbar';
import { INVALID_SESSION_ERROR_MESSAGE } from '../utils/constants/commonConstants';
import { ManageTeamDrawer } from '../common/ManageTeamDrawer/ManageTeamDrawer';
import DealApplicationDrawer from '../common/DealApplicationDrawer/DealApplicationDrawer';
import { fetchLenderConfig, fetchOrgInfo } from '../store/investor/action';
import { useParams } from 'react-router-dom';
import { fetchCompanyUploadConfig, fetchUploadJourneyData } from '../store/company/action';
import { assert } from 'console';
import { CONSTANTS } from '../store/investor/constants';

interface OrgInfo {
    owners?: {
        leadOwner?: string;
    };
}

const PORTFOLIO_PATHS = {
    DATA_ROOM: 'data-room',
    DEBT_PRODUCTS: 'debt-products',
    COMPANY_DEALS: 'company-deals',
} as const;

const hasStorageAuthData = (): boolean => {
    const storageUserData = localStorage.getItem('user_data') ?? sessionStorage.getItem('user_data');
    const authToken = localStorage.getItem('auth_token') ?? sessionStorage.getItem('auth_token');
    const orgIdFromStorage = localStorage.getItem('org_id') ?? sessionStorage.getItem('org_id');
    return !!(storageUserData || (authToken && orgIdFromStorage));
};

function CompanyPrivateRoutes() {
    const dispatch = useAppDispatch();
    const location = useLocation();

    const manageUserDrawerState = useAppSelector((state) => state.company.showManageUser);

    const navigate = useNavigate();
    const user = useAppSelector((state) => state.user);
    const userData = user.userData;
    const ip = user.ip;

    const [searchParams, setSearchParams] = useSearchParams();
    const orgId = searchParams.get('orgId') as string;
    const userId = searchParams.get('userId') as string;
    const token = searchParams.get('token') as string;
    const accountType = searchParams.get('accountType') as string;
    const parentOrgId = searchParams.get('parentOrgId') as string;
    const primaryEmail = searchParams.get('primaryEmail') as string;
    const userName = searchParams.get('userName') as string;
    const parentEmailId = searchParams.get('parentEmailId') as string;
    const userRole = searchParams.get('userRole') as string;

    const isSummon = !!(orgId && userId && token);

    // Initialize auth state from storage (runs once on mount) - prevents logout on page refresh
    const [hasStorageAuth] = useState(hasStorageAuthData);

    const isPortfolioPage =
        location.pathname.includes(PORTFOLIO_PATHS.DATA_ROOM) || location.pathname.includes(PORTFOLIO_PATHS.COMPANY_DEALS);

    // To handle Portfolio Details page on company side to avoid Unauthorized error

    const params = useParams();
    const isPortfolioDetailsPage = location.pathname.includes('/company/portfolio/');
    const isCompany = userData?.accountType === USER_TYPE.COMPANY;

    const orgInfo = useAppSelector((state): OrgInfo => state.investor.orgInfo);
    const ownerId = orgInfo?.owners?.leadOwner;
    const platformUserData = useAppSelector((state) => state.investor.platformUserData);

    useEffect(() => {
        if (isCompany && isPortfolioDetailsPage && params.id && userData?.accountType) {
            dispatch(
                fetchUploadJourneyData({
                    accountType: userData.accountType,
                    assesseeOrgId: params.id as string,
                }),
            );

            dispatch(
                fetchCompanyUploadConfig({
                    accountType: userData.accountType,
                    primaryEmail: userData.primaryEmail ?? userData.investorPrimaryEmail,
                    investorOrgId: userData.investorOrgId,
                    configName: 'AICA_BORROWER_ONBOARDING_DATA_BY_BORROWER',
                    assesseeOrgId: params.id as string,
                }),
            );
        }
    }, [isCompany, isPortfolioDetailsPage, params.id, userData?.accountType, userData?.investorOrgId]); // Use specific fields to prevent loop

    // Fetch the AICA Debt Product config to show / Hide the Debt Product tab for assessees
    useEffect(() => {
        const fetchDebtProductConfig = () => {
            const assesseeOrgId = userData?.assesseeOrgId || orgId;

            // Safely parse user_data from storage, fallback to empty object on parse error
            let savedUserData: Record<string, any> = {};
            try {
                const storedData = localStorage.getItem('user_data') || sessionStorage.getItem('user_data');
                if (storedData) {
                    savedUserData = JSON.parse(storedData);
                }
            } catch {
                // Invalid JSON in storage - use empty object
                savedUserData = {};
            }

            const accountType = userData?.accountType || savedUserData?.accountType;
            const investorOrgId = userData?.investorOrgId || savedUserData?.investorOrgId;
            const email =
                userData?.primaryEmail ||
                userData?.investorPrimaryEmail ||
                savedUserData?.primaryEmail ||
                savedUserData?.investorPrimaryEmail;
            const role = userData?.roles?.[0] || savedUserData?.roles?.[0] || userRole;

            if (accountType && investorOrgId && assesseeOrgId) {
                dispatch(
                    fetchLenderConfig({
                        accountType: accountType,
                        primaryEmail: email,
                        investorOrgId: investorOrgId,
                        assesseeOrgId: assesseeOrgId,
                        userRole: role,
                        configName: 'AICA_DEBT_PRODUCT',
                    }),
                );

                dispatch(
                    fetchLenderConfig({
                        accountType: accountType,
                        primaryEmail: email,
                        investorOrgId: investorOrgId,
                        assesseeOrgId: assesseeOrgId,
                        userRole: role,
                        configName: 'AICA_DASHBOARD',
                    }),
                );
            }
        };
        fetchDebtProductConfig();
    }, [
        userData?.assesseeOrgId,
        userData?.accountType,
        userData?.investorOrgId,
        userData?.primaryEmail,
        userData?.investorPrimaryEmail,
        userData?.roles?.[0],
        location.pathname,
        orgId,
        userRole,
    ]);

    useEffect(() => {
        if (isSummon) {
            summonProcess();
            return;
        }

        // Only redirect if we have NO authentication data (neither Redux nor storage)
        if (!userData?.userId && !hasStorageAuth) {
            toast.error(INVALID_SESSION_ERROR_MESSAGE);
            setTimeout(() => {
                navigate('/login');
            }, 1000);
        }
    }, [isSummon, userData?.userId, hasStorageAuth]);

    const summonProcess = () => {
        // Set values in session storage
        sessionStorage.setItem('auth_token', token);
        sessionStorage.setItem('org_id', orgId);
        sessionStorage.setItem('user_id', userId);

        // Set values in headers
        addTokenToHeaders(axios, orgId, token, userId);

        setSearchParams(searchParams);
        let isMagicLink = searchParams.get('configName') === 'MAGIC_LINK';
        dispatch(
            fetchConfigAfterLogin(
                { accountType, primaryEmail, investorOrgId: parentOrgId, userRole: userRole },
                {
                    onSuccess: (data) => {
                        const updatedUserData = {
                            accountType: accountType,
                            investorOrgId: data.investorOrgId,
                            assesseeOrgId: orgId,
                            primaryEmail: primaryEmail,
                            userId: userId,
                            userName: userName,
                            investorPrimaryEmail: data.investorPrimaryEmail,
                            applicationStatus: data.status,
                            lenderOrgId: data.lenderOrgId,
                            partnerOrgId: data.partnerOrgId, // Added partnerOrgId here
                        };
                        dispatch(setUserData(updatedUserData));
                        sessionStorage.setItem('user_data', JSON.stringify(updatedUserData));
                        handleRedirection(accountType, data.status, false, orgId, false, '', isMagicLink);

                        // Remove query params
                        removeSummonParams();
                    },
                    onError: () => {
                        // Remove query params
                        removeSummonParams();
                    },
                },
            ),
        );
    };

    useEffect(() => {
        if (userData?.userId && userData?.assesseeOrgId) {
            hotjarIdentify(userData.userId, userData.assesseeOrgId);
        }
    }, [userData?.userId, userData?.assesseeOrgId]);

    useEffect(() => {
        const userType = userData?.accountType;
        if (userData && ip && userType === USER_TYPE.COMPANY) {
            Mixpanel.identify(userData?.userId);
            Mixpanel.people.set({
                investorId: userData?.investorOrgId,
                companyId: userData?.assesseeOrgId,
                ip: ip,
                userType: USER_TYPE.COMPANY,
                $name: userData?.userId,
                $distinct_id: userData?.userId,
            });
            Mixpanel.register({
                InvestorOrgId: userData?.investorOrgId,
                AssesseeOrgId: userData?.assesseeOrgId,
                UserId: userData?.userId,
                userType: USER_TYPE.COMPANY,
                name: userData?.userId,
            });
        }
    }, [userData?.assesseeOrgId, ip]);

    useEffect(() => {
        if (userData?.assesseeOrgId) {
            dispatch(
                fetchOrgInfo({
                    orgId: userData.assesseeOrgId,
                }),
            );
        }
    }, [userData?.assesseeOrgId]);
    useEffect(() => {
        if (ownerId) {
            dispatch({
                type: CONSTANTS.FETCH_PLATFORM_USER_DETAILS,
                payload: { userId: ownerId },
            });
        }
    }, [ownerId]);

    const removeSummonParams = () => {
        searchParams.delete('orgId');
        searchParams.delete('userId');
        searchParams.delete('token');
        searchParams.delete('accountType');
        searchParams.delete('parentOrgId');
        searchParams.delete('primaryEmail');
        searchParams.delete('userName');
        searchParams.delete('parentEmailId');
        searchParams.delete('userRole');
    };

    return (
        <div
            className="CompanyPrivateRoutes"
            style={{
                marginTop: location.pathname.includes('portfolio') || location.pathname.includes('deals') ? '72px' : '0px',
            }}
        >
            {(searchParams.get('magicUpload') === 'true' || searchParams.get('needInfo') === 'true') && <MagicUpload />}
            {!isPortfolioPage && (
                <Topbar
                    ownerInfo={{
                        ownertype: 'leadOwner',
                        name: platformUserData?.name || '',
                        mobileNumber: platformUserData?.mobileNumber || '',
                        countryCode: platformUserData?.countryCode || '',
                        emailId: platformUserData?.emailId || '',
                    }}
                />
            )}

            {manageUserDrawerState && (
                <ManageTeamDrawer selectedOrgName={userData?.userName} orgId={userData?.assesseeOrgId} userType={userData?.accountType} />
            )}
            <DealApplicationDrawer />
            <Outlet />
        </div>
    );
}

export default CompanyPrivateRoutes;
